import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CustomTheme } from '../types/theme';
import { clientThemeRegistry, ClientId } from '../design-system/themes/client-overrides';
import { createThemeConfig } from '../design-system/themes/utils/createThemeConfig';

interface ThemeStore {
  theme: CustomTheme;
  mode: 'light' | 'dark';
  clientId: ClientId;
  isLoading: boolean;
  error: string | null;
  setTheme: (theme: CustomTheme) => void;
  setMode: (mode: 'light' | 'dark') => void;
  toggleMode: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  loadThemeForHost: (hostPrefix: string) => Promise<void>;
  // TEMP: for testing themes in localhost
  setClientId: (clientId: ClientId) => void;
}

const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: createThemeConfig(clientThemeRegistry.default) as CustomTheme,
      mode: 'light',
      clientId: 'default',
      isLoading: false,
      error: null,

      setTheme: (theme) => set({ theme, error: null }),

      setMode: (mode) => {
        const { clientId } = get();
        const clientOverrides = clientThemeRegistry[clientId];
        const theme = createThemeConfig(clientOverrides, mode);
        set({ mode, theme: theme as CustomTheme });
      },

      toggleMode: () => {
        const currentMode = get().mode;
        const newMode = currentMode === 'light' ? 'dark' : 'light';
        get().setMode(newMode);
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error, isLoading: false }),

      loadThemeForHost: async (hostPrefix: string) => {
        set({ isLoading: true, error: null });

        try {
          // Use registry lookup instead of dynamic import
          const clientId = (hostPrefix in clientThemeRegistry ? hostPrefix : 'default') as ClientId;
          const clientOverrides = clientThemeRegistry[clientId];
          const theme = createThemeConfig(clientOverrides, get().mode);

          set({
            theme: theme as CustomTheme,
            clientId,
            isLoading: false,
            error: null
          });
        } catch (error) {
          console.warn(`Theme for '${hostPrefix}' not found, using default`, error);
          set({
            theme: createThemeConfig(clientThemeRegistry.default, get().mode) as CustomTheme,
            clientId: 'default',
            isLoading: false,
            error: `Failed to load theme for ${hostPrefix}`
          });
        }
      },

      // TEMP: for testing themes in localhost
      setClientId: (clientId: ClientId) => {
        const clientOverrides = clientThemeRegistry[clientId];
        const theme = createThemeConfig(clientOverrides, get().mode);
        set({ clientId, theme: theme as CustomTheme });
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ mode: state.mode, clientId: state.clientId }),
    }
  )
);

export default useThemeStore;
